'use client';

import { useEffect } from 'react';
import { X, Check, Minus } from 'lucide-react';
import Link from 'next/link';
import { Boat } from '@/types';
import { useCompare } from './CompareContext';

interface Row {
  label: string;
  /** Restituisce stringa o boolean (boolean → ✓/–). */
  get: (b: Boat) => string | number | boolean | null | undefined;
}

const ROWS: Row[] = [
  { label: 'Lunghezza', get: (b) => b.length ? `${b.length} m` : null },
  { label: 'Capacità', get: (b) => b.capacity ? `${b.capacity} ospiti` : null },
  { label: 'Cabine', get: (b) => b.cabins ?? null },
  { label: 'Posti letto', get: (b) => b.berths ?? null },
  { label: 'Bagni', get: (b) => b.bathrooms ?? null },
  { label: 'Anno', get: (b) => b.year ?? null },
  { label: 'Marca', get: (b) => b.manufacturer || null },
  { label: 'Modello', get: (b) => b.model || null },
  { label: 'Motore', get: (b) => b.engine || (b.enginePower ? `${b.enginePower} CV` : null) },
  { label: 'Pescaggio', get: (b) => b.draft ? `${b.draft} m` : null },
  { label: 'Larghezza', get: (b) => b.beam ? `${b.beam} m` : null },
  { label: 'Patente nautica', get: (b) => b.licenseRequired },
  { label: 'Skipper disponibile', get: (b) => b.skipperAvailable },
  { label: 'Skipper obbligatorio', get: (b) => b.skipperRequired },
  { label: 'Hostess', get: (b) => b.hostessAvailable },
  { label: 'Cauzione', get: (b) => b.securityDeposit ? `€${b.securityDeposit}` : null },
  { label: 'Carburante incluso', get: (b) => !b.fuelNotIncluded },
  { label: 'Porto', get: (b) => b.port || b.location || null },
  { label: 'Rating', get: (b) => b.rating ? `${b.rating} ★ (${b.reviews ?? 0})` : null },
  { label: 'Prezzo / giorno', get: (b) => `€${b.price}` },
];

const renderCell = (val: string | number | boolean | null | undefined) => {
  if (val === null || val === undefined || val === '') return <Minus className="w-4 h-4 text-slate-300 mx-auto" />;
  if (typeof val === 'boolean') {
    return val ? <Check className="w-5 h-5 text-emerald-600 mx-auto" /> : <Minus className="w-4 h-4 text-slate-300 mx-auto" />;
  }
  return <span className="text-sm font-semibold text-slate-700">{val}</span>;
};

export default function CompareModal() {
  const { modalOpen, closeModal, selected, remove } = useCompare();

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [modalOpen, closeModal]);

  if (!modalOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={closeModal}>
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-xl font-display font-bold text-slate-900">Confronto imbarcazioni</h2>
          <button onClick={closeModal} className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full">
            <thead className="sticky top-0 bg-white z-10 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-3 w-40 text-xs font-bold uppercase tracking-wider text-slate-500">Caratteristica</th>
                {selected.map((b) => (
                  <th key={b.id} className="px-4 py-3 min-w-[180px] text-center align-bottom">
                    <div className="flex flex-col items-center gap-2">
                      {b.image && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={b.image} alt={b.name} className="w-32 h-20 object-cover rounded-xl shadow-sm" />
                      )}
                      <Link href={`/boat/${b.slug ?? b.id}`} className="text-sm font-bold text-blue-700 hover:underline text-center line-clamp-2">
                        {b.name}
                      </Link>
                      <button
                        onClick={() => remove(b.id)}
                        className="text-[10px] font-semibold text-slate-400 hover:text-red-600 uppercase tracking-wider"
                      >
                        Rimuovi
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.label} className="border-b border-slate-100 even:bg-slate-50/40">
                  <td className="px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">{row.label}</td>
                  {selected.map((b) => (
                    <td key={b.id} className="px-4 py-3 text-center">{renderCell(row.get(b))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
